import { Type } from '@sinclair/typebox';
import { createPathGuidParameter } from '../../routes/createPathParameter';
import { createdResponse, notFoundResponse, updatedResponse } from '../../routes/defaultResponses';
import { inspectionFormTableSchema } from './inspectionFormTableSchema';

export const inspectionFormPaths = {
    '/api/settings/inspection-forms': {
        get: {
            operationId: 'inspectionForm.list',
            parameters: [
                {
                    name: 'searchParameters',
                    in: 'query',
                    schema: Type.Object({
                        objectTypeId: Type.Optional(Type.String()),
                        inspectionStandardId: Type.Optional(Type.String()),
                        templateId: Type.Optional(Type.String()),
                    }),
                },
            ],
            responses: {
                '200': {
                    description: 'List of inspection forms',
                    content: {
                        'application/json': {
                            schema: Type.Array(
                                Type.Object({
                                    id: Type.String({ format: 'uuid' }),
                                    name: Type.String(),
                                    objectType: Type.String(),
                                    standard: Type.String(),
                                    template: Type.String(),
                                }),
                            ),
                        },
                    },
                },
            },
        },

        post: {
            operationId: 'inspectionForm.create',
            parameters: [],
            requestBody: {
                content: {
                    'application/json': {
                        schema: Type.Omit(inspectionFormTableSchema, ['id', 'isActive']),
                    },
                },
            },
            responses: {
                ...createdResponse,
                ...notFoundResponse,
            },
        },
    },

    '/api/inspection-forms/{objectId}': {
        get: {
            operationId: 'inspectionForm.getListForUserAndObject',
            parameters: [createPathGuidParameter('objectId')],
            responses: {
                '200': {
                    description: 'List of inspection forms',
                    content: {
                        'application/json': {
                            schema: Type.Array(
                                Type.Object({
                                    id: Type.String({ format: 'uuid' }),
                                    name: Type.String(),
                                }),
                            ),
                        },
                    },
                },
            },
        },
    },

    '/api/settings/inspection-forms/{inspectionFormId}': {
        get: {
            operationId: 'inspectionForm.get',
            parameters: [createPathGuidParameter('inspectionFormId')],
            responses: {
                '200': {
                    description: 'An inspection form',
                    content: {
                        'application/json': {
                            schema: inspectionFormTableSchema,
                        },
                    },
                },
                ...notFoundResponse,
            },
        },

        post: {
            operationId: 'inspectionForm.update',
            parameters: [createPathGuidParameter('inspectionFormId')],
            requestBody: {
                content: {
                    'application/json': {
                        schema: Type.Omit(inspectionFormTableSchema, ['id', 'isActive']),
                    },
                },
            },
            responses: {
                ...updatedResponse,
                ...notFoundResponse,
            },
        },

        delete: {
            operationId: 'inspectionForm.remove',
            parameters: [createPathGuidParameter('inspectionFormId')],
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Object({ ok: Type.Boolean() }),
                        },
                    },
                },
            },
        },
    },

    '/api/settings/inspection-forms/{inspectionFormId}/companies': {
        get: {
            operationId: 'inspectionForm.getCompanies',
            parameters: [createPathGuidParameter('inspectionFormId')],
            responses: {
                '200': {
                    description: 'A list of companies',
                    content: {
                        'application/json': {
                            schema: Type.Array(
                                Type.Object({
                                    id: Type.String(),
                                    name: Type.String(),
                                }),
                            ),
                        },
                    },
                },
                ...notFoundResponse,
            },
        },
    },

    '/api/settings/inspection-forms/{inspectionFormId}/companies/{companyId}': {
        post: {
            operationId: 'inspectionForm.addCompany',
            parameters: [createPathGuidParameter('inspectionFormId'), createPathGuidParameter('companyId')],
            responses: {
                '200': {
                    description: 'True for success',
                    content: {
                        'application/json': {
                            schema: Type.Boolean(),
                        },
                    },
                },
                ...notFoundResponse,
            },
        },

        delete: {
            operationId: 'inspectionForm.removeCompany',
            parameters: [createPathGuidParameter('inspectionFormId'), createPathGuidParameter('companyId')],
            responses: {
                '200': {
                    description: 'True for success',
                    content: {
                        'application/json': {
                            schema: Type.Boolean(),
                        },
                    },
                },
                ...notFoundResponse,
            },
        },
    },
} as const;
